'use client';

import { Route } from 'next';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';

/**
 * Custom hook for managing state based on URL query parameters.
 *
 * @template T - The type of the state value.
 * @param {string} key - The key of the query parameter.
 * @param {T} fallback - The fallback value for the state.
 * @returns {[T, (newState: T) => void]} - A tuple containing the current state value and a function to update the state.
 */
export const useParamState = <T>(key: string, fallback: T): [T, (newState: T) => void] => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const newSearchParams = new URLSearchParams(searchParams.toString());

  const paramValue = searchParams.get(key);
  let state: T;
  try {
    state = paramValue !== null ? (JSON.parse(paramValue) as T) : fallback;
  } catch {
    state = fallback;
  }

  const setState = (newState: T) => {
    if (newState !== undefined && newState !== null && newState !== fallback) {
      newSearchParams.set(key, JSON.stringify(newState));

      router.replace(`${pathname}?${newSearchParams.toString()}` as Route, {
        scroll: false,
      });
    } else {
      newSearchParams.delete(key);

      router.replace(`${pathname}?${newSearchParams.toString()}` as Route, {
        scroll: false,
      });
    }
  };

  return [state, setState] as const;
};
